import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usuariosAPI } from '../services/api';
import { useSesion } from '../context/SesionContext';
import { formatVsPar } from '../utils/handicap';
import './MisPartidas.css';

const ESTADOS = {
  esperando: 'Sin empezar',
  en_curso: 'En curso',
  finalizada: 'Finalizada'
};

function MisPartidas() {
  const navigate = useNavigate();
  const { usuario, cargando: cargandoSesion } = useSesion();
  const [partidas, setPartidas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (cargandoSesion) return;

    if (!usuario) {
      navigate('/cuenta');
      return;
    }

    Promise.all([usuariosAPI.misPartidas(), usuariosAPI.misEstadisticas()])
      .then(([partidasResp, statsResp]) => {
        setPartidas(partidasResp.data);
        setEstadisticas(statsResp.data);
      })
      .catch(err => {
        console.error('Error al cargar el historial:', err);
        setError('No se pudo cargar tu historial');
      })
      .finally(() => setCargando(false));
  }, [usuario, cargandoSesion, navigate]);

  const fecha = (partida) => {
    const valor = partida.fecha_inicio || partida.created_at;
    if (!valor) return '';
    return new Date(valor).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const redondear = (valor, decimales = 1) =>
    valor === null || valor === undefined ? '—' : Number(valor).toFixed(decimales);

  if (cargando || cargandoSesion) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#2e7d32' }}>Cargando tu historial...</p>
      </div>
    );
  }

  return (
    <div className="mis-partidas">
      <div className="header">
        <h1>Mis partidas</h1>
        <p>{usuario?.nombre}</p>
      </div>

      <div className="container">
        {error && <div className="error">{error}</div>}

        {estadisticas && estadisticas.partidas > 0 && (
          <div className="card fade-in">
            <h2 className="bloque-titulo">Tus números</h2>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-valor">{estadisticas.partidas}</div>
                <div className="stat-etiqueta">Partidas</div>
              </div>
              <div className="stat">
                <div className="stat-valor">{estadisticas.victorias}</div>
                <div className="stat-etiqueta">Ganadas</div>
              </div>
              <div className="stat">
                <div className="stat-valor">{redondear(estadisticas.media_golpes, 0)}</div>
                <div className="stat-etiqueta">Golpes de media</div>
              </div>
              <div className="stat">
                <div className="stat-valor">{estadisticas.mejor_vuelta ?? '—'}</div>
                <div className="stat-etiqueta">Mejor vuelta</div>
              </div>
              <div className="stat">
                <div className="stat-valor">{formatVsPar(estadisticas.mejor_vs_par)}</div>
                <div className="stat-etiqueta">Mejor sobre tu par</div>
              </div>
              <div className="stat">
                <div className="stat-valor">{redondear(estadisticas.media_stableford, 0)}</div>
                <div className="stat-etiqueta">Stableford medio</div>
              </div>
            </div>

            {estadisticas.hoyos_flojos?.length > 0 && (
              <div className="hoyos-flojos">
                <h3>Donde más golpes pierdes</h3>
                {estadisticas.hoyos_flojos.map(hoyo => (
                  <div key={hoyo.hoyo} className="hoyo-flojo">
                    <span className="hoyo-flojo-num">Hoyo {hoyo.hoyo}</span>
                    <span className="hoyo-flojo-dato">
                      par {hoyo.par} · media {redondear(hoyo.media_golpes)} golpes
                      {' '}(+{redondear(hoyo.media_sobre_par)})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="card">
          <h2 className="bloque-titulo">Historial</h2>

          {partidas.length === 0 ? (
            <div className="sin-partidas">
              <p>Todavía no has jugado ninguna partida con tu cuenta.</p>
              <button className="btn btn-primary" onClick={() => navigate('/crear')}>
                Crear una partida
              </button>
            </div>
          ) : (
            <div className="partidas-lista">
              {partidas.map(partida => (
                <div key={partida.jugador_id} className="partida-item">
                  <div className="partida-cabecera">
                    <span className="partida-fecha">{fecha(partida)}</span>
                    <span className={`partida-estado ${partida.estado}`}>
                      {ESTADOS[partida.estado] || partida.estado}
                    </span>
                  </div>

                  <div className="partida-campo">{partida.campo_nombre}</div>

                  {partida.estado === 'finalizada' && partida.total_golpes !== null ? (
                    <div className="partida-datos">
                      <span><strong>{partida.total_golpes}</strong> golpes</span>
                      <span>su par {partida.par_personal}</span>
                      <span className="partida-vspar">{formatVsPar(partida.vs_par)}</span>
                      <span>{partida.puntos_stableford} pts</span>
                      {partida.posicion && (
                        <span className="partida-posicion">
                          {partida.posicion}º de {partida.num_jugadores}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="partida-datos">
                      <span>Handicap {partida.handicap}</span>
                      <span>{partida.num_jugadores} jugadores</span>
                      {partida.estado !== 'finalizada' && (
                        <button
                          className="partida-volver"
                          onClick={() => navigate(`/juego/${partida.codigo}`)}
                        >
                          Continuar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          className="btn btn-secondary btn-full"
          onClick={() => navigate('/')}
          style={{ marginTop: '12px' }}
        >
          Volver al inicio
        </button>
      </div>
    </div>
  );
}

export default MisPartidas;
